import type {
  MIServerEntry, MIServerStatus, MIServerRegistryOptions,
  MIServerType, MIOGCCapabilities
} from './types';

let idCounter = 0;
function generateId(): string {
  return `server_${++idCounter}_${Date.now().toString(36)}`;
}

export class MIServerRegistry extends EventTarget {
  private servers: Map<string, MIServerEntry> = new Map();
  private statuses: Map<string, MIServerStatus> = new Map();
  private healthCheckTimer: ReturnType<typeof setInterval> | null = null;
  private options: Required<MIServerRegistryOptions>;
  private listeners: Map<string, Set<Function>> = new Map();

  constructor(options?: MIServerRegistryOptions) {
    super();
    this.options = {
      healthCheckIntervalMs: options?.healthCheckIntervalMs ?? 60000,
      maxRetries: options?.maxRetries ?? 3,
      retryDelayMs: options?.retryDelayMs ?? 2000,
      timeoutMs: options?.timeoutMs ?? 30000,
      enableAutoDiscovery: options?.enableAutoDiscovery ?? true,
    };
  }

  register(entry: MIServerEntry): string {
    const id = entry.id || generateId();
    const serverEntry = { ...entry, id };
    this.servers.set(id, serverEntry);
    this.statuses.set(id, {
      serverId: id,
      connected: false,
      lastChecked: new Date(),
      capabilities: [],
    });
    this.emit('server:registered', { serverId: id, entry: serverEntry });
    return id;
  }

  unregister(serverId: string): void {
    this.servers.delete(serverId);
    this.statuses.delete(serverId);
    this.emit('server:unregistered', { serverId });
  }

  update(serverId: string, updates: Partial<MIServerEntry>): void {
    const existing = this.servers.get(serverId);
    if (existing) {
      this.servers.set(serverId, { ...existing, ...updates });
    }
  }

  get(serverId: string): MIServerEntry | undefined {
    return this.servers.get(serverId);
  }

  getAll(): MIServerEntry[] {
    return Array.from(this.servers.values());
  }

  getByType(type: MIServerType): MIServerEntry[] {
    return this.getAll().filter(s => s.type === type);
  }

  getByCapability(capability: string): MIServerEntry[] {
    return this.getAll().filter(s => {
      const status = this.statuses.get(s.id!);
      return status?.capabilities?.includes(capability);
    });
  }

  async connect(serverId: string): Promise<MIServerStatus> {
    const server = this.servers.get(serverId);
    if (!server) throw new Error(`Server ${serverId} not found`);

    const start = Date.now();
    try {
      const url = this.buildProxyUrl(server, server.url);
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), this.options.timeoutMs);

      const headers: Record<string, string> = { ...server.headers };
      this.applyAuth(headers, server);

      const response = await fetch(url, {
        method: 'HEAD',
        headers,
        signal: controller.signal,
        mode: 'cors',
      }).catch(() => fetch(url, { headers, signal: controller.signal }));

      clearTimeout(timeout);

      const status: MIServerStatus = {
        serverId,
        connected: response.ok || response.status === 405,
        lastChecked: new Date(),
        latencyMs: Date.now() - start,
        capabilities: this.inferCapabilities(server),
      };
      this.statuses.set(serverId, status);
      this.emit('server:connected', { serverId, status });
      return status;
    } catch (err: any) {
      const status: MIServerStatus = {
        serverId,
        connected: false,
        lastChecked: new Date(),
        latencyMs: Date.now() - start,
        error: err.message || 'Connection failed',
      };
      this.statuses.set(serverId, status);
      this.emit('server:error', { serverId, error: err.message });
      return status;
    }
  }

  disconnect(serverId: string): void {
    const status = this.statuses.get(serverId);
    if (status) {
      status.connected = false;
      this.statuses.set(serverId, status);
      this.emit('server:disconnected', { serverId });
    }
  }

  async reconnect(serverId: string): Promise<MIServerStatus> {
    this.disconnect(serverId);
    return this.connect(serverId);
  }

  getStatus(serverId: string): MIServerStatus {
    return this.statuses.get(serverId) || {
      serverId,
      connected: false,
      lastChecked: new Date(),
      error: 'Server not registered',
    };
  }

  getAllStatuses(): Map<string, MIServerStatus> {
    return new Map(this.statuses);
  }

  async healthCheck(serverId?: string): Promise<Map<string, MIServerStatus>> {
    const results = new Map<string, MIServerStatus>();
    const serverIds = serverId ? [serverId] : Array.from(this.servers.keys());

    await Promise.allSettled(
      serverIds.map(async (id) => {
        const status = await this.connect(id);
        results.set(id, status);
        this.emit('server:healthcheck', { serverId: id, status });
      })
    );

    return results;
  }

  setHealthCheckInterval(ms: number): void {
    this.stopHealthChecks();
    this.healthCheckTimer = setInterval(() => this.healthCheck(), ms);
  }

  stopHealthChecks(): void {
    if (this.healthCheckTimer) {
      clearInterval(this.healthCheckTimer);
      this.healthCheckTimer = null;
    }
  }

  async discoverCapabilities(serverId: string): Promise<MIOGCCapabilities | null> {
    const server = this.servers.get(serverId);
    if (!server) return null;

    try {
      const capabilities = this.inferCapabilities(server);
      const status = this.statuses.get(serverId);
      if (status) {
        status.capabilities = capabilities;
        this.statuses.set(serverId, status);
      }
      return {
        service: server.type,
        version: '1.0.0',
        title: server.name,
        layers: [],
      };
    } catch {
      return null;
    }
  }

  on(event: string, handler: Function): string {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(handler);
    return `${event}_${Date.now()}`;
  }

  off(event: string, handler: Function): void {
    this.listeners.get(event)?.delete(handler);
  }

  private emit(event: string, data: any): void {
    this.listeners.get(event)?.forEach(handler => {
      try { handler(data); } catch {}
    });
  }

  private inferCapabilities(server: MIServerEntry): string[] {
    const caps: string[] = [];
    switch (server.type) {
      case 'ogc-wms': caps.push('WMS', 'GetMap', 'GetFeatureInfo'); break;
      case 'ogc-wfs': caps.push('WFS', 'GetFeature', 'DescribeFeatureType'); break;
      case 'ogc-wmts': caps.push('WMTS', 'GetTile'); break;
      case 'ogc-wcs': caps.push('WCS', 'GetCoverage'); break;
      case 'ogc-features': caps.push('OGCFeatures', 'Collections', 'Items'); break;
      case '3dtiles': caps.push('3DTiles', 'Tileset'); break;
      case 'cesium-ion': caps.push('3DTiles', 'Terrain', 'Imagery'); break;
      case 'xyz': caps.push('XYZ', 'Tiles'); break;
      case 'arcgis-rest': case 'arcgis-map': caps.push('ArcGIS', 'MapServer', 'Tiles'); break;
      case 'arcgis-feature': caps.push('ArcGIS', 'FeatureServer', 'Query'); break;
      case 'arcgis-image': caps.push('ArcGIS', 'ImageServer'); break;
      case 'geoserver': caps.push('WMS', 'WFS', 'WMTS', 'WCS'); break;
      case 'migis': caps.push('REST', 'Tiles', 'WebSocket', 'Analysis'); break;
      default: caps.push('Custom');
    }
    return caps;
  }

  buildProxyUrl(server: MIServerEntry, targetUrl: string): string {
    if (server.proxy?.enabled) {
      if (server.proxy.mode === 'custom' && server.proxy.proxyUrl) {
        return `${server.proxy.proxyUrl}?url=${encodeURIComponent(targetUrl)}`;
      }
      return `/api/v2/proxy?url=${encodeURIComponent(targetUrl)}`;
    }
    return targetUrl;
  }

  applyAuth(headers: Record<string, string>, server: MIServerEntry): void {
    if (!server.auth) return;
    switch (server.auth.type) {
      case 'api-key':
        headers[server.auth.apiKeyHeader || 'X-API-Key'] = server.auth.apiKey || '';
        break;
      case 'bearer':
        headers['Authorization'] = `Bearer ${server.auth.token}`;
        break;
      case 'basic':
        headers['Authorization'] = `Basic ${btoa(`${server.auth.username}:${server.auth.password}`)}`;
        break;
    }
  }
}
